import { action, thunk } from 'easy-peasy';
import { utilityThrowError } from '@utils/errors';
import type {
  TJobClientsResponse,
  TJobClientsStore,
  TJobClient,
} from '@model/Job/clients/Clients.type';

export const clientsStore: TJobClientsStore = {
  clients: [],

  setClients: action((state, payload) => {
    state.clients = payload;
  }),

  getClients: thunk(async (actions, payload, helpers) => {
    const _actions = helpers.getStoreActions();
    const { jobId } = payload;

    _actions.job.setLoading(true);
    _actions.job.setError(false);

    try {
      const res = (await _actions.session.authFetch({
        endpoint: `/v1/employer-view/jobs/${jobId}/clients`,
      })) as TJobClientsResponse;

      actions.setClients(res.results);
    } catch (error) {
      _actions.job.setError(true);
      utilityThrowError(error, 'Error to get clients');
    } finally {
      _actions.job.setLoading(false);
    }
  }),

  postClients: thunk(async (actions, payload, helpers) => {
    const _actions = helpers.getStoreActions();
    const currentItems = helpers.getStoreState().job.clients.clients;
    const { jobId, ...rest } = payload;

    _actions.job.setLoading(true);
    _actions.job.setError(false);

    try {
      const res = await _actions.session.authFetch({
        endpoint: `/v1/employer-view/jobs/${jobId}/clients`,
        requestOptions: { method: 'POST', body: JSON.stringify(rest) },
      });

      actions.setClients([
        ...((currentItems || []) as TJobClient[]),
        res as TJobClient,
      ]);
      return res;
    } catch (error) {
      _actions.job.setError(true);
      utilityThrowError(error, 'Error to save client');
      return null;
    }
  }),

  putClients: thunk(async (actions, payload, helpers) => {
    const _actions = helpers.getStoreActions();
    const currentItems = helpers.getStoreState().job.clients.clients;
    const { jobId, clientId, ...rest } = payload;

    _actions.job.setLoading(true);
    _actions.job.setError(false);

    try {
      const res = await _actions.session.authFetch({
        endpoint: `/v1/employer-view/jobs/${jobId}/clients/${clientId}`,
        requestOptions: { method: 'PUT', body: JSON.stringify(rest) },
      });

      const result = currentItems.map(i =>
        i.clientId === clientId ? res : i,
      ) as TJobClient[];

      actions.setClients(result);
      return res;
    } catch (error) {
      _actions.job.setError(true);
      utilityThrowError(error, 'Error to update client');
      return null;
    }
  }),

  deleteClients: thunk(async (actions, payload, helpers) => {
    const { clientId, jobId } = payload;
    const _actions = helpers.getStoreActions();
    const currentItems = helpers.getStoreState().job.clients.clients;
    try {
      await _actions.session.authFetch({
        endpoint: `/v1/employer-view/jobs/${jobId}/clients/${clientId}`,
        requestOptions: { method: 'DELETE' },
        skipHandleResponse: true,
      });

      actions.setClients(
        (currentItems || []).filter(i => i.clientId !== clientId),
      );
    } catch (error) {
      _actions.job.setError(true);
      utilityThrowError(error, 'Error to delete client');
    }
  }),
};
